import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider } from '@mui/material';
import { Box } from '@mui/system';

import BookingContactForm from '../types/bookingContactForm.type';
import AddContactForm from './AddContactForm';

type AddContactFormListProps = {
  contactsList?: BookingContactForm[];
  contactsListErrors?: {
    id: string;
    booking_contact_id: string;
    calendar_ids: string;
  }[];
  getContacts: (contacts: ({ id: string } & BookingContactForm)[]) => void;
};

const AddContactFormList: FC<AddContactFormListProps> = ({
  contactsList,
  contactsListErrors,
  getContacts,
}) => {
  const { t } = useTranslation(['bookingsServices', 'common']);

  const [contactsForms, setContactsForms] = useState<
    ({ id: string } & BookingContactForm)[]
  >([
    {
      id: Date.now().toString(),
      booking_contact_id: '',
      calendar_ids: [],
    },
  ]);
  const [errorMessage, setErrorMessage] = useState<
    { id: string; message: string }[]
  >([]);

  useEffect(() => getContacts(contactsForms), [contactsForms, getContacts]);

  const addContactForm = () => {
    const updatedContactsForms = [
      ...contactsForms,
      {
        id: Date.now().toString(),
        booking_contact_id: '',
        calendar_ids: [],
      },
    ];

    setContactsForms(updatedContactsForms);
    getContacts(updatedContactsForms);
  };

  const removeContactForm = (id: string) => {
    const updatedContactsForms = contactsForms.filter((form) => form.id !== id);

    setContactsForms(updatedContactsForms);
    getContacts(updatedContactsForms);
  };

  const checkIsContactIdAlreadyExist = (
    contactId: string,
    currentFormId: string,
  ) => {
    return contactsForms.some(
      (form) =>
        form.booking_contact_id === contactId && form.id !== currentFormId,
    );
  };

  const onUpdateContactForm = (values: BookingContactForm, id: string) => {
    setErrorMessage((prev) => prev.filter((error) => error.id !== id));

    const updatedContactsForms = contactsForms.map((contactForm) =>
      contactForm.id === id ? { ...contactForm, ...values } : contactForm,
    );

    setContactsForms(updatedContactsForms);
    getContacts(updatedContactsForms);

    if (checkIsContactIdAlreadyExist(values.booking_contact_id, id)) {
      setErrorMessage((prev) => [
        ...prev,
        { id, message: t('bookingsServices:providedContactAlreadyAdded') },
      ]);
      return;
    }
  };

  return (
    <Box>
      {contactsForms.map((form) => (
        <AddContactForm
          contactsList={contactsList}
          formErrors={contactsListErrors?.find((error) => error.id === form.id)}
          key={form.id}
          onRemove={() => removeContactForm(form.id)}
          error={errorMessage.find((error) => error.id === form.id)?.message}
          getFormValues={(values) => onUpdateContactForm(values, form.id)}
        />
      ))}

      <Box
        sx={{
          px: 1,
          position: 'sticky',
          bottom: -20,
          backgroundColor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Divider sx={{ mx: -1, pt: 1, mb: 1 }} />

        <Button onClick={addContactForm}>
          {t('bookingsServices:addContact')}
        </Button>
      </Box>
    </Box>
  );
};

export default AddContactFormList;
